import { useEffect, useRef, useState } from "react";
import {
    MessageCircle,
    X,
    Send,
    Phone,
    MessageSquare,
    Bot,
    User2,
} from "lucide-react";
import api from "@/lib/api";

const SUPPORT_PHONE = "+9609478000";
const SUPPORT_VIBER = "+9609478000";

const genSessionId = () =>
    `pz-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function ChatbotWidget() {
    const [open, setOpen] = useState(false);
    const [sessionId] = useState(genSessionId);
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            content: "Hi, how can we help?",
            handoff: false,
        },
    ]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, open, loading]);

    const send = async (e) => {
        e?.preventDefault();

        const text = input.trim();

        if (!text || loading) return;

        setMessages((m) => [...m, { role: "user", content: text }]);
        setInput("");
        setLoading(true);

        try {
            const res = await api.post("/chat", {
                session_id: sessionId,
                message: text,
            });

            setMessages((m) => [
                ...m,
                {
                    role: "assistant",
                    content: res.data.reply,
                    handoff: !!res.data.handoff,
                },
            ]);
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;

            if (status === 503) {
                setMessages((m) => [
                    ...m,
                    {
                        role: "assistant",
                        content:
                            (detail ||
                                "Our AI assistant is not configured yet.") +
                            " You can still reach our team directly:",
                        handoff: true,
                    },
                ]);
            } else {
                setMessages((m) => [
                    ...m,
                    {
                        role: "assistant",
                        content:
                            "Sorry, I'm having trouble right now. You can reach our team directly.",
                        handoff: true,
                    },
                ]);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* FAB */}
            <button
                onClick={() => setOpen(true)}
                data-testid="chatbot-open-btn"
                className={`fixed z-40 bottom-5 right-5 w-14 h-14 rounded-full bg-pz-primary text-white shadow-xl grid place-items-center hover:bg-pz-primaryHover hover:-translate-y-1 transition-all ${
                    open ? "scale-0" : "scale-100"
                }`}
                aria-label="Open chat"
            >
                <MessageCircle size={24} />
                <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-pz-warning animate-pulse-soft" />
            </button>

            {/* Panel */}
            <div
                data-testid="chatbot-panel"
                className={`fixed z-50 bottom-5 right-5 w-[calc(100vw-2.5rem)] sm:w-96 max-w-md h-[560px] max-h-[calc(100vh-2.5rem)] bg-white rounded-3xl shadow-2xl border border-pz-border flex flex-col overflow-hidden origin-bottom-right transition-all duration-300 ${
                    open
                        ? "scale-100 opacity-100 pointer-events-auto"
                        : "scale-95 opacity-0 pointer-events-none"
                }`}
            >
                {/* Header */}
                <div className="bg-pz-text text-white p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-pz-primary grid place-items-center">
                            <Bot size={18} />
                        </div>

                        <div>
                            <h3 className="font-display text-xl leading-none">
                                Pizano Helper
                            </h3>

                            <p className="text-[11px] text-white/60 uppercase tracking-widest mt-1">
                                Online · usually replies fast
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={() => setOpen(false)}
                        data-testid="chatbot-close-btn"
                        className="text-white/70 hover:text-white"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Messages */}
                <div
                    ref={scrollRef}
                    className="flex-1 overflow-y-auto p-4 space-y-3 bg-pz-bg"
                >
                    {messages.map((m, i) => (
                        <div
                            key={i}
                            className={`flex gap-2 ${
                                m.role === "user"
                                    ? "justify-end"
                                    : "justify-start"
                            }`}
                        >
                            {m.role === "assistant" && (
                                <div className="w-7 h-7 rounded-full bg-pz-primary text-white grid place-items-center shrink-0">
                                    <Bot size={14} />
                                </div>
                            )}

                            <div
                                className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                                    m.role === "user"
                                        ? "bg-pz-primary text-white rounded-br-md"
                                        : "bg-white text-pz-text border border-pz-border rounded-bl-md"
                                }`}
                                data-testid={`chat-msg-${i}`}
                            >
                                {m.content}

                                {m.handoff && (
                                    <div className="mt-3 grid grid-cols-2 gap-2">
                                        <a
                                            href={`viber://chat?number=${SUPPORT_VIBER}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid="chat-viber-btn"
                                            className="inline-flex items-center justify-center gap-1.5 bg-pz-success text-white text-xs uppercase tracking-wider font-bold py-2 rounded-full hover:opacity-90"
                                        >
                                            <MessageSquare size={12} />
                                            Viber
                                        </a>

                                        <a
                                            href={`tel:${SUPPORT_PHONE}`}
                                            data-testid="chat-call-btn"
                                            className="inline-flex items-center justify-center gap-1.5 bg-pz-text text-white text-xs uppercase tracking-wider font-bold py-2 rounded-full hover:bg-pz-primary"
                                        >
                                            <Phone size={12} />
                                            Call
                                        </a>
                                    </div>
                                )}
                            </div>

                            {m.role === "user" && (
                                <div className="w-7 h-7 rounded-full bg-pz-bg2 text-pz-text grid place-items-center shrink-0">
                                    <User2 size={14} />
                                </div>
                            )}
                        </div>
                    ))}

                    {loading && (
                        <div className="flex gap-2 justify-start">
                            <div className="w-7 h-7 rounded-full bg-pz-primary text-white grid place-items-center shrink-0">
                                <Bot size={14} />
                            </div>

                            <div className="bg-white text-pz-text border border-pz-border px-4 py-3 rounded-2xl rounded-bl-md">
                                <div className="flex gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-pz-textMuted animate-bounce" />
                                    <span
                                        className="w-1.5 h-1.5 rounded-full bg-pz-textMuted animate-bounce"
                                        style={{ animationDelay: "0.1s" }}
                                    />
                                    <span
                                        className="w-1.5 h-1.5 rounded-full bg-pz-textMuted animate-bounce"
                                        style={{ animationDelay: "0.2s" }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Input */}
                <form
                    onSubmit={send}
                    className="p-3 border-t border-pz-border bg-white flex items-center gap-2"
                >
                    <input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Ask anything..."
                        data-testid="chatbot-input"
                        className="flex-1 bg-pz-bg2 rounded-full px-4 py-2.5 text-sm outline-none border border-transparent focus:border-pz-primary text-pz-text placeholder:text-pz-textMuted"
                    />

                    <button
                        type="submit"
                        disabled={loading || !input.trim()}
                        data-testid="chatbot-send-btn"
                        className="w-10 h-10 grid place-items-center rounded-full bg-pz-primary text-white hover:bg-pz-primaryHover disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Send size={16} />
                    </button>
                </form>
            </div>
        </>
    );
}
